/**
 * Campaign parameter handling — FAAS-765 (event firing) and FAAS-782 (Portal handover).
 *
 * Portal captures campaign attribution server-side on every request in
 * `Web_Plugin_GoogleAnalytics::preDispatch` (giffgaff-fe
 * `app/modules/web/plugins/GoogleAnalytics.php`). When a visitor lands on this
 * MFE instead, the MFE consumes the query string and internal navigation into
 * Portal carries clean URLs, so that plugin never sees the parameters and
 * order-success attribution plus Awin conversions go dark.
 *
 * The bridge is Portal's own existing fallback: `GoogleAnalytics.php:66` reads a
 * `__ggUtmz` cookie when no campaign parameter is on the request and no campaign
 * is in session. Nothing in giffgaff-fe writes that cookie today, so this MFE
 * becomes its first writer.
 */

/** UTM parameters. `utm_content` is included here but is not part of Portal's session copy. */
export const UTM_PARAMS = ['utm_id', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

/**
 * Affiliate and click-ID parameters, taken from the field list
 * `Web_Plugin_GoogleAnalytics::validCampaign()` validates.
 */
export const AFFILIATE_PARAMS = [
  'awc',
  'gclid',
  'dclid',
  'pub',
  'sv_campaign_id',
  'ggCampaign',
  'placementID',
  'CreativeID',
] as const

/**
 * Click IDs that mark a paid landing but that Portal neither validates nor reads.
 *
 * Deliberately separate from `AFFILIATE_PARAMS`, which is the field list
 * `validCampaign()` validates and is re-exported as `PORTAL_AFFILIATE_PARAMS` for the
 * CTC drift guard. Putting `msclkid` in that list would claim a Portal contract that
 * does not exist.
 *
 * It is used by `hasTrackingParams` only, and is NOT fed into `resolveCampaignToken`.
 * Portal has no `msclkid` branch, so synthesising a campaign name from one here would
 * hand BI a campaign for an MFE landing and `(direct)` for the identical Portal
 * landing, which is the one property the parity test compares. The cookie stays empty
 * for a bare Bing click, exactly as it would have been without this MFE in the path.
 *
 * `gclsrc` is not listed: Google emits it alongside `gclid`, never on its own, so it
 * would never be the parameter that decides anything.
 */
export const CLICK_ID_ONLY_PARAMS = ['msclkid'] as const

/** Name Portal reads the tokenised campaign tags from (`GoogleAnalytics.php:21`). */
export const GG_UTMZ_COOKIE = '__ggUtmz'

/**
 * True when the landing URL carries campaign attribution of any kind.
 *
 * Drives the `initiate_tracking` event (FAAS-765). Wider than the fe-devices
 * original, which checks the five UTM keys only — an affiliate landing
 * (`?awc=...`) carries no UTM parameters at all but is still a tracked landing, and
 * neither does a Bing click auto-tagged with `msclkid` alone.
 */
export function hasTrackingParams(search: string): boolean {
  const params = new URLSearchParams(search)

  return [...UTM_PARAMS, ...AFFILIATE_PARAMS, ...CLICK_ID_ONLY_PARAMS].some((key) => params.has(key))
}

/**
 * Prepares one token value for the cookie.
 *
 * Percent-encoded rather than reduced to a narrow allowlist, so the value Portal
 * ends up with matches what it would have derived from the URL itself. PHP
 * urldecodes `$_COOKIE` before `Web_Plugin_GoogleAnalytics` sees it, so
 * `black%20friday` arrives as `black friday`, which `filterCampaign()` keeps
 * (space is in its VALID_CHARACTERS). Stripping the space instead would hand BI
 * a different campaign name for an MFE landing than for a Portal landing, on the
 * exact field the parity test compares.
 *
 * `,` and `:` are removed before encoding, not encoded. They are the delimiters
 * `tokenizeCampaignTags()` splits on, and since PHP decodes before that split,
 * an encoded `%2C` would decode back into a delimiter and break the tokens apart.
 * Portal's own filter keeps both characters, so a campaign name containing one
 * is the single remaining divergence. Campaign names do not contain them in
 * practice, and the alternative is corrupting the whole cookie.
 */
function encodeToken(value: string): string {
  return encodeURIComponent(value.replace(/[,:]/g, ''))
}

/**
 * Resolves the `campaign` token, mirroring the precedence of the branch ladder
 * in `GoogleAnalytics.php:47-80` so the value Portal ends up with matches what
 * it would have derived from the parameters itself.
 *
 * The `-lastclick` / `-attributed` suffix is deliberately NOT applied — Portal
 * appends `-attributed` itself on the cookie path (`GoogleAnalytics.php:75-76`).
 */
function resolveCampaignToken(params: URLSearchParams): string | undefined {
  const base =
    params.get('ggCampaign') ||
    params.get('utm_campaign') ||
    (params.get('gclid') ? 'paid_search' : '') ||
    (params.get('dclid') ? 'Display' : '')

  if (!base) {
    return undefined
  }

  // Portal appends both of these to the campaign name (`GoogleAnalytics.php:83-84`)
  // from the *current* request's parameters. Navigation from this MFE into Portal
  // carries clean URLs, so folding them into the token is the only way they survive.
  const suffixes = [params.get('placementID'), params.get('CreativeID')]
    .filter((value): value is string => Boolean(value))
    .map(encodeToken)
    .filter(Boolean)

  return [encodeToken(base), ...suffixes].filter(Boolean).join('-')
}

/**
 * Builds the `__ggUtmz` cookie value in Portal's tokenised format:
 * comma-separated `key:value` pairs (`GoogleAnalytics.php:157-177`).
 *
 * Only the three keys Portal's tokeniser actually reads are emitted —
 * `campaign`, `awc` and `sv_campaign_id`. Note `pub` (the Awin publisher ID) is
 * captured by the parameter path but is NOT read back from the cookie, so it
 * cannot be bridged this way; see the note on FAAS-782.
 *
 * @returns The cookie value, or `undefined` when the URL carries nothing Portal
 *   would read — in which case no cookie should be written at all, since Portal
 *   treats the cookie's absence as meaningful (`HandsetOrderDetails.php:125`).
 */
export function buildGgUtmzValue(search: string): string | undefined {
  const params = new URLSearchParams(search)

  const tokens: string[] = []

  const campaign = resolveCampaignToken(params)

  if (campaign) {
    tokens.push(`campaign:${campaign}`)
  }

  for (const key of ['awc', 'sv_campaign_id'] as const) {
    const value = encodeToken(params.get(key) ?? '')

    if (value) {
      tokens.push(`${key}:${value}`)
    }
  }

  return tokens.length > 0 ? tokens.join(',') : undefined
}

/** 180 days, matching `expirationDays = t.expirationDays || 180` in Portal's gg-utmz.min.js. */
export const GG_UTMZ_MAX_AGE_SECONDS = 180 * 24 * 60 * 60

export type GgUtmzCookieOptions = {
  /** Must be the shared parent domain so Portal receives the cookie. */
  cookieDomain: string
  maxAgeSeconds?: number
  /** Omit `Secure` in local development, where the cookie is set over http. */
  secure?: boolean
}

/**
 * Serialises the `__ggUtmz` cookie, or its expiry when `value` is `null`.
 *
 * Shared by the SSR middleware and the client-side consent sync so the two can
 * never drift on domain, path or lifetime. A mismatch there would mean the
 * client writing a second cookie beside the server's rather than replacing it,
 * and Portal reading whichever the browser happened to send first.
 *
 * Not `HttpOnly`: the GTM container reads this cookie, and the consent sync has
 * to be able to expire it from the page.
 */
export function buildGgUtmzCookie(
  value: string | null,
  { cookieDomain, maxAgeSeconds = GG_UTMZ_MAX_AGE_SECONDS, secure = true }: GgUtmzCookieOptions
): string {
  const age = value === null ? 0 : maxAgeSeconds

  return [
    `${GG_UTMZ_COOKIE}=${value ?? ''}`,
    `Domain=${cookieDomain}`,
    'Path=/',
    `Max-Age=${age}`,
    'SameSite=Lax',
    ...(secure ? ['Secure'] : []),
  ].join('; ')
}

/**
 * Hostname labels Portal's script treats as search engines, verbatim from the list in
 * `gg-utmz.min.js`. A referrer matches if *any* dot-separated label is in this set, so
 * `news.google.co.uk` is organic on the `google` label.
 */
const SEARCH_ENGINE_LABELS = new Set([
  'google',
  'yahoo',
  'bing',
  'baidu',
  'ask',
  'aol',
  'haosou',
  'yandex',
  'sogou',
  'so',
  'seznam',
  'babylon',
  'naver',
  'comcast',
  'startsiden',
  'daum',
  'rambler',
  'virgilio',
  'kvasir',
  'duckduckgo',
])

/**
 * Hostname labels that mean a payment round trip rather than a new visit, from the same
 * script. Its list repeats several entries; duplicates are dropped here because a Set
 * makes them meaningless.
 */
const PAYMENT_GATEWAY_LABELS = new Set([
  'paypal',
  'arcot',
  'cardinalcommerce',
  'securesuite',
  'secure',
  'secure5',
  'secure6',
  'secure7',
  'clicksafe',
  'verifiedbyvisa',
  'verifiedby',
  'visaverifiedbyvisa',
  'nortonsafe',
  'maybankard3dsecure',
  'acs',
  'acs3',
  'acs4',
  'giffgaff',
  'peer2peer',
])

/** Hostname with a leading `www.` removed, which is how the script keys everything. */
const bareHostname = (url: string): string | undefined => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return undefined
  }
}

const hasLabelIn = (hostname: string, labels: Set<string>): boolean =>
  hostname.split('.').some((label) => labels.has(label))

/**
 * Mirrors `isSameDomain`: equal hostnames, or a referrer on a giffgaff subdomain.
 *
 * The subdomain arm really is `indexOf('.giffgaff.com')` on the referrer only, so
 * `community.giffgaff.com` counts and the bare apex is caught by the equality arm instead.
 */
export function isSameSiteReferrer(referrer: string, currentUrl: string): boolean {
  const from = bareHostname(referrer)
  const to = bareHostname(currentUrl)

  if (!from || !to) {
    return false
  }

  return from === to || from.includes('.giffgaff.com')
}

/** Mirrors `referedFromPaymentGateway`. */
export function isPaymentGatewayReferrer(referrer: string): boolean {
  const hostname = bareHostname(referrer)

  return hostname ? hasLabelIn(hostname, PAYMENT_GATEWAY_LABELS) : false
}

/**
 * Mirrors `loadFromReferrerString`: `(organic)` for a search engine, otherwise
 * `(referral)-<hostname>`.
 *
 * Returned bare rather than tokenised, deliberately. Portal only prefixes `campaign:` in
 * the URL-parameter branch; `tokenizeCampaignTags()` returns null for a value with no
 * colon and `GoogleAnalytics.php:78` then takes the bare-string path, which is what puts
 * `(organic)` into the session verbatim.
 */
export function buildReferrerValue(referrer: string): string | undefined {
  const hostname = bareHostname(referrer)

  if (!hostname) {
    return undefined
  }

  return hasLabelIn(hostname, SEARCH_ENGINE_LABELS) ? '(organic)' : `(referral)-${hostname}`
}

/** What the caller should do with the cookie on this request. */
export type GgUtmzDecision = { action: 'keep' } | { action: 'write'; value: string }

/**
 * Decides the `__ggUtmz` value for one page load, mirroring `runDefaultBehaviour` in
 * Portal's gg-utmz.min.js branch for branch.
 *
 * This MFE is the only writer on its own pages: it does not load the styleguide script,
 * so anything the script would have done here has to be done by us or not at all. Before
 * this existed we wrote only the tagged-campaign branch, which meant an organic or
 * referral landing left no cookie, and Portal then saw its own domain as the referrer on
 * the next hop and recorded `(direct)`.
 */
export function resolveGgUtmzDecision({
  search,
  referrer,
  currentUrl,
  hasExistingCookie,
}: {
  search: string
  referrer: string | undefined
  currentUrl: string
  hasExistingCookie: boolean
}): GgUtmzDecision {
  // 1. An existing cookie survives an internal hop or a payment round trip, even when the
  //    URL carries campaign parameters. This is what protects attribution across PayPal
  //    and 3-D Secure, so it is checked before the tagged branch, not after.
  if (
    hasExistingCookie &&
    referrer &&
    (isSameSiteReferrer(referrer, currentUrl) || isPaymentGatewayReferrer(referrer))
  ) {
    return { action: 'keep' }
  }

  // 2. Tagged campaign. Last touch wins, overwriting whatever was there.
  const tagged = buildGgUtmzValue(search)

  if (tagged) {
    return { action: 'write', value: tagged }
  }

  // 3. A genuinely external referrer.
  if (referrer && !isSameSiteReferrer(referrer, currentUrl)) {
    const value = buildReferrerValue(referrer)

    if (value) {
      return { action: 'write', value }
    }
  }

  // 4. Nothing new to record and something already there.
  if (hasExistingCookie) {
    return { action: 'keep' }
  }

  // 5. No parameters, no usable referrer, no cookie.
  return { action: 'write', value: '(direct)' }
}
